import React from 'react';
import { Helmet } from 'react-helmet-async';
import './ProductPageTemplate.css';
import DemoButton from '../Common/DemoButton';


const REGIONAL_PRODUCTS = ['forwarding', 'customs', 'finance'];
const REGION_PREFIXES = ['in', 'uae', 'us'];

function buildProductHreflang(pageUrl) {
  const path = pageUrl.replace('https://shipmnts.com', '');
  const segments = path.split('/').filter(Boolean);
  const hasPrefix = REGION_PREFIXES.includes(segments[0]);
  const productPath = hasPrefix ? '/' + segments.slice(1).join('/') : path;
  const productSlug = segments[hasPrefix ? segments.length - 1 : segments.length - 1];
  if (!REGIONAL_PRODUCTS.includes(productSlug)) return null;
  return [
    { hreflang: 'en-IN',     href: `https://shipmnts.com/in${productPath}` },
    { hreflang: 'en-AE',     href: `https://shipmnts.com/uae${productPath}` },
    { hreflang: 'en-US',     href: `https://shipmnts.com/us${productPath}` },
    { hreflang: 'x-default', href: `https://shipmnts.com${productPath}` },
  ];
}

const ProductPageTemplate = ({
  productName,
  tagline,
  description,
  metaDescription,
  icon,
  features = [],
  faqs = [],
  pageUrlOverride,
  currency = 'INR',
}) => {
  const pageUrl = pageUrlOverride || `https://shipmnts.com/products/${productName.toLowerCase().replace(/\s+/g, '-')}`;
  const hreflangLinks = buildProductHreflang(pageUrl);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://shipmnts.com' },
      { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://shipmnts.com/#products' },
      { '@type': 'ListItem', position: 3, name: productName, item: pageUrl },
    ],
  };

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `Shipmnts ${productName}`,
    url: pageUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Android, iOS',
    description: metaDescription || tagline,
    featureList: features.map(f => f.title),
    provider: { '@id': 'https://shipmnts.com/#organization' },
    offers: {
      '@type': 'Offer',
      description: 'Free demo available. Contact for pricing.',
      url: 'https://shipmnts.com/demo',
    },
  };

  const faqSchema = faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  } : null;

  return (
    <section className="product-page-container">
      <Helmet>
        <title>{productName} | Shipmnts</title>
        <meta name="description" content={metaDescription || tagline} />
        <link rel="canonical" href={pageUrl} />
        {hreflangLinks && hreflangLinks.map(({ hreflang, href }) => (
          <link key={hreflang} rel="alternate" hreflang={hreflang} href={href} />
        ))}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      {/* Hero Section */}
      <div className="product-hero">
        <div className="product-hero-content">
          <div className="product-icon-container">
            {typeof icon === 'string' ? (
              <img src={icon} alt={productName} className="product-icon" />
            ) : (
              React.createElement(icon, { className: 'product-icon', alt: productName })
            )}
          </div>
          <h1 className="product-title">{productName}</h1>
          <p className="product-tagline">{tagline}</p>
          <p className="product-description">{description}</p>
          <div className="cta-container">
            <DemoButton
              label="Book a Demo"
            />
          </div>
        </div>
      </div>

      {/* Features Section */}
      {features.length > 0 && (
        <div className="product-features-section">
          <h2 className="section-heading">Key Features</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div className="feature-card" key={index}>
                {feature.image && (
                  <div className="feature-image-container">
                    <img src={feature.image} alt={feature.title} className="feature-image" />
                  </div>
                )}
                <h3 className="feature-title">{feature.title}</h3>
                {feature.description && (
                  <p className="feature-description">{feature.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="product-faq-section">
          <h2 className="section-heading">Frequently Asked Questions</h2>
          <div className="product-faq-list">
            {faqs.map(({ question, answer }, i) => (
              <details key={i} className="product-faq-item">
                <summary className="product-faq-q">{question}</summary>
                <p className="product-faq-a">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductPageTemplate;
